import React, { useState } from 'react';
import { Users, AlertTriangle, Activity, Heart, Lightbulb, Moon, Coffee, ChevronRight, ArrowLeft, Filter, Search, Clock, UserCheck, CalendarClock, AlertCircle } from 'lucide-react';
import FamilyCaregiverPortal from './FamilyCaregiverPortal';

export interface Resident {
  id: string;
  name: string;
  age: number;
  roomNumber: string;
  status: 'stable' | 'needs_attention' | 'urgent';
  alerts: {
    type: 'behavioral' | 'health' | 'safety' | 'environmental';
    severity: 'low' | 'medium' | 'high';
    message: string;
    timestamp: Date;
  }[];
  vitals: {
    heartRate: number;
    bloodPressure: string;
    temperature: number;
    lastChecked: Date;
  };
  lastActivity: {
    type: string;
    location: string;
    timestamp: Date;
  };
  metrics: {
    cognitiveScore: number;
    mobilityScore: number;
    socialScore: number;
    wellnessScore: number;
  };
}

export interface StaffMember {
  id: string;
  name: string;
  role: 'nurse' | 'caregiver' | 'specialist';
  shift: 'morning' | 'afternoon' | 'night';
  assignedResidents: string[];
}

export interface StaffingRequirement {
  shift: 'morning' | 'afternoon' | 'night';
  requiredNurses: number;
  requiredCaregivers: number;
  currentNurses: number;
  currentCaregivers: number;
}

// Mock data generation
export const generateMockResidents = (count: number): Resident[] => {
  return Array.from({ length: count }, (_, i) => ({
    id: `R${(i + 1).toString().padStart(3, '0')}`,
    name: `${['Alice', 'Bob', 'Carol', 'David', 'Emma', 'Frank', 'Grace', 'Henry', 'Isabel', 'John'][i % 10]} ${['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'][i % 10]}`,
    age: 65 + Math.floor(Math.random() * 25),
    roomNumber: `${Math.floor(i / 10) + 1}${(i % 10) + 1}`,
    status: ['stable', 'needs_attention', 'urgent'][Math.floor(Math.random() * 3)] as 'stable' | 'needs_attention' | 'urgent',
    alerts: Array.from({ length: Math.floor(Math.random() * 3) }, () => ({
      type: ['behavioral', 'health', 'safety', 'environmental'][Math.floor(Math.random() * 4)] as 'behavioral' | 'health' | 'safety' | 'environmental',
      severity: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)] as 'low' | 'medium' | 'high',
      message: [
        'Unusual movement pattern detected',
        'Missed medication schedule',
        'Room temperature outside normal range',
        'Decreased social interaction',
        'Changes in sleep pattern'
      ][Math.floor(Math.random() * 5)],
      timestamp: new Date(Date.now() - Math.random() * 86400000)
    })),
    vitals: {
      heartRate: 60 + Math.floor(Math.random() * 30),
      bloodPressure: `${110 + Math.floor(Math.random() * 30)}/${70 + Math.floor(Math.random() * 20)}`,
      temperature: 97.5 + Math.random() * 2,
      lastChecked: new Date(Date.now() - Math.random() * 3600000)
    },
    lastActivity: {
      type: ['Walking', 'Sleeping', 'Eating', 'Social Activity', 'Therapy'][Math.floor(Math.random() * 5)],
      location: ['Room', 'Common Area', 'Dining Room', 'Garden', 'Activity Room'][Math.floor(Math.random() * 5)],
      timestamp: new Date(Date.now() - Math.random() * 3600000)
    },
    metrics: {
      cognitiveScore: Math.floor(Math.random() * 100),
      mobilityScore: Math.floor(Math.random() * 100),
      socialScore: Math.floor(Math.random() * 100),
      wellnessScore: Math.floor(Math.random() * 100)
    }
  }));
};

export const generateMockStaffing = (): {
  currentStaff: StaffMember[];
  requirements: StaffingRequirement[];
} => {
  const shifts: ('morning' | 'afternoon' | 'night')[] = ['morning', 'afternoon', 'night'];
  const roles: ('nurse' | 'caregiver' | 'specialist')[] = ['nurse', 'caregiver', 'specialist'];
  const names = ['Sarah Johnson', 'Michael Chen', 'Emily Rodriguez', 'James Wilson', 'Maria Garcia', 'David Kim'];

  const currentStaff = Array.from({ length: 8 }, (_, i) => ({
    id: `S${(i + 1).toString().padStart(3, '0')}`,
    name: names[i % names.length],
    role: roles[i % roles.length],
    shift: shifts[Math.floor(i / 3)],
    assignedResidents: Array.from({ length: 4 }, () => `R${(Math.floor(Math.random() * 20) + 1).toString().padStart(3, '0')}`)
  }));

  const requirements = shifts.map(shift => ({
    shift,
    requiredNurses: 3,
    requiredCaregivers: 4,
    currentNurses: currentStaff.filter(s => s.shift === shift && s.role === 'nurse').length,
    currentCaregivers: currentStaff.filter(s => s.shift === shift && s.role === 'caregiver').length
  }));

  return { currentStaff, requirements };
};

const ResidentMonitoring: React.FC = () => {
  const [residents] = useState<Resident[]>(generateMockResidents(20));
  const [staffing] = useState(generateMockStaffing());
  const [selectedResident, setSelectedResident] = useState<Resident | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'stable' | 'needs_attention' | 'urgent'>('all');

  const filteredResidents = residents.filter(resident => {
    const matchesSearch = resident.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resident.roomNumber.includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || resident.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (selectedResident) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="p-6">
          <button
            onClick={() => setSelectedResident(null)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft size={20} />
            Back to All Residents
          </button>
          <FamilyCaregiverPortal />
        </div>
      </div>
    );
  }

  const getShiftStatus = (requirement: StaffingRequirement) => {
    const nurseStatus = requirement.currentNurses >= requirement.requiredNurses;
    const caregiverStatus = requirement.currentCaregivers >= requirement.requiredCaregivers;
    if (nurseStatus && caregiverStatus) return 'optimal';
    if (requirement.currentNurses === 0 || requirement.currentCaregivers === 0) return 'critical';
    return 'needs_staff';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold flex items-center gap-2 mb-2">
          <Users className="text-purple-600" />
          Resident Monitoring Dashboard
        </h1>
        <p className="text-gray-600">
          Monitoring {residents.length} residents • {residents.filter(r => r.status === 'urgent').length} need immediate attention
        </p>
      </div>

      {/* Staffing Overview */}
      <div className="mb-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <UserCheck className="text-purple-600" />
          Today's Staffing Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {staffing.requirements.map(req => (
            <div key={req.shift} className={`p-4 rounded-lg border ${
              getShiftStatus(req) === 'optimal' ? 'border-green-200 bg-green-50' :
              getShiftStatus(req) === 'critical' ? 'border-red-200 bg-red-50' :
              'border-yellow-200 bg-yellow-50'
            }`}>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold capitalize">{req.shift} Shift</h3>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  getShiftStatus(req) === 'optimal' ? 'bg-green-100 text-green-800' :
                  getShiftStatus(req) === 'critical' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {getShiftStatus(req) === 'optimal' ? 'Fully Staffed' :
                   getShiftStatus(req) === 'critical' ? 'Critical' : 'Needs Staff'}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Nurses</span>
                  <span className="text-sm font-medium">
                    {req.currentNurses}/{req.requiredNurses}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Caregivers</span>
                  <span className="text-sm font-medium">
                    {req.currentCaregivers}/{req.requiredCaregivers}
                  </span>
                </div>
              </div>
              <div className="mt-3 text-sm">
                {staffing.currentStaff
                  .filter(staff => staff.shift === req.shift)
                  .map(staff => (
                    <div key={staff.id} className="flex items-center gap-2 text-gray-600">
                      <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                      {staff.name} ({staff.role})
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by name or room number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
          className="border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="all">All Statuses</option>
          <option value="stable">Stable</option>
          <option value="needs_attention">Needs Attention</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>

      {/* Resident Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredResidents.map(resident => (
          <div
            key={resident.id}
            className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer ${
              resident.status === 'urgent' ? 'bg-red-50 border-2 border-red-200' :
              resident.status === 'needs_attention' ? 'bg-yellow-50 border-2 border-yellow-200' :
              'bg-green-50 border-2 border-green-200'
            }`}
            onClick={() => setSelectedResident(resident)}
          >
            <div className="p-4">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-lg">{resident.name}</h3>
                  <p className="text-sm text-gray-600">Room {resident.roomNumber}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm ${
                  resident.status === 'urgent' ? 'bg-red-100 text-red-800' :
                  resident.status === 'needs_attention' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {resident.status.replace('_', ' ').charAt(0).toUpperCase() + resident.status.slice(1).replace('_', ' ')}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="text-sm text-gray-600">Heart Rate</div>
                  <div className="font-semibold">{resident.vitals.heartRate} bpm</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Blood Pressure</div>
                  <div className="font-semibold">{resident.vitals.bloodPressure}</div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Activity className="text-purple-600" size={16} />
                  <span className="text-sm">
                    Last active: {resident.lastActivity.type} in {resident.lastActivity.location}
                  </span>
                </div>
                {resident.alerts.length > 0 && (
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertTriangle size={16} />
                    <span className="text-sm">{resident.alerts.length} active alerts</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t">
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center">
                    <div className="text-xs text-gray-600">Cognitive</div>
                    <div className="font-semibold text-sm">{resident.metrics.cognitiveScore}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-600">Mobility</div>
                    <div className="font-semibold text-sm">{resident.metrics.mobilityScore}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-600">Social</div>
                    <div className="font-semibold text-sm">{resident.metrics.socialScore}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-600">Wellness</div>
                    <div className="font-semibold text-sm">{resident.metrics.wellnessScore}%</div>
                  </div>
                </div>
              </div>
            </div>

            <div className={`px-4 py-3 rounded-b-lg flex justify-end ${
              resident.status === 'urgent' ? 'bg-red-100/50' :
              resident.status === 'needs_attention' ? 'bg-yellow-100/50' :
              'bg-green-100/50'
            }`}>
              <ChevronRight size={20} className="text-gray-400" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResidentMonitoring; 
